import { useCallback, useEffect, useMemo, useState } from 'react';
import { DialogActions, DialogContent, DialogTitle, Paper, Stack } from '@mui/material';
import { ContainedButton, Dialog, TextButton } from '@shared/components';
import { SupplierItemCalculation } from '@retail/my-assortment/types';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { alphabeticSort } from '@shared/utils';
import { SupplierPriorityRow } from './SupplierPriorityRow';
import { SupplierPrioritySection } from './SupplierPrioritySection';
import { getInitialTier, SupplierPriorityItem, SupplierPriorityTier } from './supplierPriorityTier';

interface Props {
  isOpen: boolean;
  supplierItems: Partial<SupplierItemCalculation>[];
  isSaving?: boolean;
  onClose: () => void;
  onSave: (priorities: SupplierPriorityItem[]) => void;
}

export function SupplierPriorityDialog({
  isOpen,
  supplierItems,
  isSaving,
  onClose,
  onSave,
}: Props) {
  const t = useMyAssortmentTFunction();

  const initialItems = useMemo<SupplierPriorityItem[]>(
    () =>
      supplierItems
        .filter(
          (item): item is Partial<SupplierItemCalculation> & { mgSupplierItemId: number } =>
            item.mgSupplierItemId != null
        )
        .map((item) => ({
          mgSupplierItemId: item.mgSupplierItemId,
          supplierName: item.supplierName ?? '',
          tier: getInitialTier({
            inAssortment: !!item.inAssortment,
            prioritised: !!item.prioritised,
          }),
        })),
    [supplierItems]
  );

  const [items, setItems] = useState<SupplierPriorityItem[]>(initialItems);

  useEffect(() => {
    if (isOpen) setItems(initialItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const setTier = useCallback((mgSupplierItemId: number, tier: SupplierPriorityTier) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.mgSupplierItemId === mgSupplierItemId) return { ...item, tier };
        if (tier === 'MAIN' && item.tier === 'MAIN') return { ...item, tier: 'PRIORITIZED' };
        return item;
      })
    );
  }, []);

  const moveUp = useCallback(
    (item: SupplierPriorityItem) =>
      setTier(item.mgSupplierItemId, item.tier === 'UNPRIORITIZED' ? 'PRIORITIZED' : 'MAIN'),
    [setTier]
  );

  const moveDown = useCallback(
    (item: SupplierPriorityItem) =>
      setTier(item.mgSupplierItemId, item.tier === 'MAIN' ? 'PRIORITIZED' : 'UNPRIORITIZED'),
    [setTier]
  );

  const byTier = useCallback(
    (tier: SupplierPriorityTier) =>
      items
        .filter((item) => item.tier === tier)
        .sort(alphabeticSort<SupplierPriorityItem>('supplierName')),
    [items]
  );

  const mainPrioritized = byTier('MAIN');
  const prioritized = byTier('PRIORITIZED');
  const unprioritized = byTier('UNPRIORITIZED');

  const renderRows = (rows: SupplierPriorityItem[]) =>
    rows.map((item) => (
      <SupplierPriorityRow
        key={item.mgSupplierItemId}
        supplier={item}
        canMoveUp={item.tier !== 'MAIN'}
        canMoveDown={item.tier !== 'UNPRIORITIZED'}
        onMoveUp={() => moveUp(item)}
        onMoveDown={() => moveDown(item)}
      />
    ));

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('myAssortment.priority.editPriority')}</DialogTitle>

      <DialogContent>
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mt: 1 }}>
          <Stack
            direction="column"
            sx={{
              '& > *:not(:first-of-type)': {
                borderTop: '1px solid',
                borderColor: 'divider',
              },
            }}
          >
            <SupplierPrioritySection
              title={t('myAssortment.priority.mainPrioritized')}
              accent="success"
              isEmpty={mainPrioritized.length === 0}
            >
              {renderRows(mainPrioritized)}
            </SupplierPrioritySection>

            <SupplierPrioritySection
              title={t('myAssortment.priority.prioritized')}
              isEmpty={prioritized.length === 0}
            >
              {renderRows(prioritized)}
            </SupplierPrioritySection>

            <SupplierPrioritySection
              title={t('myAssortment.priority.unprioritized')}
              helpText={t('myAssortment.priority.unprioritizedHelp')}
              isEmpty={unprioritized.length === 0}
            >
              {renderRows(unprioritized)}
            </SupplierPrioritySection>
          </Stack>
        </Paper>
      </DialogContent>

      <DialogActions>
        <TextButton onClick={onClose} disabled={isSaving} size="small">
          {t('myAssortment.cancel')}
        </TextButton>
        <ContainedButton loading={isSaving} onClick={() => onSave(items)} size="small">
          {t('myAssortment.save')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
}
